import React, { useState, useEffect } from 'react';
import { Download, Users, TrendingUp, Loader2, AlertCircle, Calendar } from 'lucide-react';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import api from '../services/api';

interface PrelaunchSignup {
  id: number;
  email: string;
  user_type: string;
  referral_source: string | null;
  signed_up_at: string;
  ip_address: string | null;
}

interface PrelaunchStats {
  total: number;
  total_fair_managers: number;
  total_vendors: number;
}

const PrelaunchSignupManagement: React.FC = () => {
  const [signups, setSignups] = useState<PrelaunchSignup[]>([]);
  const [stats, setStats] = useState<PrelaunchStats>({
    total: 0,
    total_fair_managers: 0,
    total_vendors: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadSignups();
  }, []);

  const loadSignups = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/admin/prelaunch-signups');

      setSignups(response.data.items);
      setStats({
        total: response.data.total,
        total_fair_managers: response.data.total_fair_managers,
        total_vendors: response.data.total_vendors
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);

      const response = await api.get('/admin/prelaunch-signups?export_csv=true', {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prelaunch_signups_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserTypeLabel = (userType: string) => {
    return userType === 'fair_manager' ? 'Fair Manager' : 'Vendor';
  };

  const getUserTypeBadgeColor = (userType: string) => {
    return userType === 'fair_manager' ? 'bg-teal-100 text-primary-dark' : 'bg-green-100 text-green-800';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pre-Launch Signups</h1>
            <p className="text-gray-600 mt-1">Manage early access email signups</p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={exporting || loading || signups.length === 0}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Export to CSV
              </>
            )}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-teal-900">Total Signups</h3>
              <Users className="w-8 h-8 text-secondary" />
            </div>
            <p className="text-3xl font-bold text-teal-900">{stats.total}</p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-teal-900">Fair Managers</h3>
              <TrendingUp className="w-8 h-8 text-secondary" />
            </div>
            <p className="text-3xl font-bold text-teal-900">{stats.total_fair_managers}</p>
            <p className="text-sm text-primary-dark mt-1">
              {stats.total > 0 ? Math.round((stats.total_fair_managers / stats.total) * 100) : 0}% of total
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">Vendors</h3>
              <Calendar className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-900">{stats.total_vendors}</p>
            <p className="text-sm text-green-700 mt-1">
              {stats.total > 0 ? Math.round((stats.total_vendors / stats.total) * 100) : 0}% of total
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
            <button
              onClick={loadSignups}
              className="text-sm text-red-600 hover:text-red-700 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      ) : (
        <>
          {/* Signups Table */}
          {signups.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Referral Source
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Signed Up At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {signups.map((signup) => (
                      <tr key={signup.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{signup.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadgeColor(signup.user_type)}`}>
                            {getUserTypeLabel(signup.user_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{signup.referral_source || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{formatDate(signup.signed_up_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600 font-mono">{signup.ip_address || '-'}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {signups.map((signup) => (
                  <div key={signup.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-sm font-medium text-gray-900">{signup.email}</div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadgeColor(signup.user_type)}`}>
                        {getUserTypeLabel(signup.user_type)}
                      </span>
                    </div>
                    {signup.referral_source && (
                      <div className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Source:</span> {signup.referral_source}
                      </div>
                    )}
                    <div className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Signed up:</span> {formatDate(signup.signed_up_at)}
                    </div>
                    {signup.ip_address && (
                      <div className="text-sm text-gray-600 font-mono">
                        <span className="font-medium">IP:</span> {signup.ip_address}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No signups yet</h3>
              <p className="text-gray-600">
                Signups will appear here as users register for early access.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrelaunchSignupManagement;
